import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usersAPI } from '../services/api';

const Manager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const timerRef = useRef(null);

  const loadData = useCallback(async (isInitial) => {
    try {
      if (isInitial) setLoading(true);
      const data = await usersAPI.getManagerView();
      if (Array.isArray(data)) {
        setUsers(data);
        setError(null);
      }
    } catch (err) {
      if (isInitial) setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(true);
    timerRef.current = setInterval(() => loadData(false), 7000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loadData]);

  const filtered = search
    ? users.filter(u =>
        u.username?.toLowerCase().includes(search.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        u.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        (u.extension_info?.extension || '').includes(search)
      )
    : users;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <p>Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#ef4444' }}>{error}</p>
        <button onClick={() => loadData(true)} style={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ margin: 0, color: '#60a5fa' }}>User Manager</h2>
          <button onClick={() => loadData(true)} style={{ backgroundColor: 'transparent', color: '#60a5fa', border: '1px solid #60a5fa', borderRadius: '8px', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>&#x21bb;</button>
        </div>
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Full Name</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Skills</th>
              <th style={styles.th}>Extension</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Group</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                  No users found
                </td>
              </tr>
            ) : (
              filtered.map(user => (
                <tr key={user.id} style={user.is_active ? {} : { opacity: 0.5 }}>
                  <td style={styles.td}>{user.username}</td>
                  <td style={styles.td}>{user.full_name || '-'}</td>
                  <td style={styles.td}>
                    {user.customer_name ? (
                      <span style={{ ...styles.badge, backgroundColor: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' }}>
                        {user.customer_name}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {user.skills?.length > 0 ? (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {user.skills.map(s => (
                          <span key={s.id} style={{ ...styles.badge, backgroundColor: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }}>
                            {s.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {user.extension_info?.extension ? (
                      <span style={{ fontFamily: 'monospace', color: '#e2e8f0', fontWeight: 600 }}>
                        {user.extension_info.extension}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {user.extension_info ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        backgroundColor: user.extension_info.status === 'AVAILABLE' ? 'rgba(52, 211, 153, 0.2)' : user.extension_info.status === 'BUSY-ONCALL' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: user.extension_info.status === 'AVAILABLE' ? '#34d399' : user.extension_info.status === 'BUSY-ONCALL' ? '#fbbf24' : '#ef4444',
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: user.extension_info.status === 'AVAILABLE' ? '#34d399' : user.extension_info.status === 'BUSY-ONCALL' ? '#fbbf24' : '#ef4444',
                          display: 'inline-block',
                        }} />
                        {user.extension_info.status === 'AVAILABLE' ? 'Available' : user.extension_info.status === 'BUSY-ONCALL' ? 'Busy - OnCall' : 'Unavailable'}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>Not registered</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {user.group_name ? (
                      <span style={{ ...styles.badge, backgroundColor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' }}>
                        {user.group_name}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  searchInput: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#e2e8f0',
    fontSize: '14px',
    minWidth: '280px',
    outline: 'none',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: '#9ca3af',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    color: '#e2e8f0',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  retryBtn: {
    marginTop: '1rem',
    padding: '8px 24px',
    backgroundColor: '#60a5fa',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
};

export default Manager;
